/**
 * Social Connections API Service
 * Handles all API calls for social connection management
 */

import { apiClient } from '@/app/api/client';
import { AxiosError } from 'axios';

// Interfaces
interface Connection {
  id: string;
  platform_type: string;
  platform_name: string;
  platform_user_id: string;
  platform_user_name: string;
  icon_url?: string;
  created_at?: string;
  updated_at?: string;
  settings?: ConnectionSettings;
}

interface ConnectionSettings {
  [key: string]: any;
}

interface AuthorizationResponse {
  authorization_url: string;
}

interface CallbackResponse {
  success: boolean;
  connection: Connection;
}

class SocialConnectionsApiService {
  private handleError(error: unknown): never {
    if (error instanceof AxiosError && error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('An unexpected API error occurred');
  }

  async listConnections(): Promise<Connection[]> {
    try {
      const response = await apiClient.get<Connection[]>('/api/user/connections');
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  async getConnection(connectionId: string): Promise<Connection> {
    try {
      const response = await apiClient.get<Connection>(`/api/user/connections/${connectionId}`);
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  async initiateOAuth(platform: string): Promise<AuthorizationResponse> {
    try {
      const response = await apiClient.post<AuthorizationResponse>(`/api/user/connections/${platform}/authorize`);
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  async handleOAuthCallback(
    platform: string,
    code: string,
    state: string
  ): Promise<CallbackResponse> {
    try {
      const response = await apiClient.post<CallbackResponse>(`/api/user/connections/${platform}/callback`, { code, state });
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  async updateConnectionSettings(
    connectionId: string,
    settings: ConnectionSettings
  ): Promise<{ success: boolean; connection: Connection }> {
    try {
      const response = await apiClient.put<{ success: boolean; connection: Connection }>(`/api/user/connections/${connectionId}/settings`, { settings });
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  async disconnectAccount(connectionId: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.delete<{ success: boolean; message: string }>(`/api/user/connections/${connectionId}`);
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  async logInterest(platform: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.post<{ success: boolean; message: string }>('/api/user/connections/log-interest', { platform });
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }
}

export const socialConnectionsApi = new SocialConnectionsApiService();
export default socialConnectionsApi;
